(() => {
  const sliderCleanups = new Map();
  const init = (root = document) => {
    root.querySelectorAll('[data-b2b-slider]:not([data-b2b-ready])').forEach((slider) => {
      slider.dataset.b2bReady = 'true';
      const slides = [...slider.querySelectorAll('[data-b2b-slide]')];
      const dots = [...slider.querySelectorAll('[data-b2b-dot]')];
      if (!slides.length) return;
      const pauseButton = slider.querySelector('[data-b2b-pause]');
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      let current = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let timer;
      let paused = false;
      let pointerInside = false;
      const show = (index) => {
        current = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === current);
          slide.setAttribute('aria-hidden', i === current ? 'false' : 'true');
          slide.inert = i !== current;
        });
        dots.forEach((dot, i) => dot.setAttribute('aria-current', i === current ? 'true' : 'false'));
      };
      const start = () => {
        clearInterval(timer);
        if (slider.dataset.autoplay === 'true' && slides.length > 1 && !paused && !pointerInside && !document.hidden && !slider.matches(':focus-within') && !reducedMotion.matches) {
          timer = setInterval(() => show(current + 1), Number(slider.dataset.interval || 5000));
        }
      };
      slider.querySelector('[data-b2b-prev]')?.addEventListener('click', () => { show(current - 1); start(); });
      slider.querySelector('[data-b2b-next]')?.addEventListener('click', () => { show(current + 1); start(); });
      dots.forEach((dot, i) => dot.addEventListener('click', () => { show(i); start(); }));
      pauseButton?.addEventListener('click', () => {
        paused = !paused;
        pauseButton.setAttribute('aria-pressed', String(paused));
        pauseButton.textContent = paused ? slider.dataset.playLabel : slider.dataset.pauseLabel;
        start();
      });
      slider.addEventListener('mouseenter', () => { pointerInside = true; clearInterval(timer); });
      slider.addEventListener('mouseleave', () => { pointerInside = false; start(); });
      slider.addEventListener('focusin', () => clearInterval(timer));
      slider.addEventListener('focusout', () => queueMicrotask(start));
      const onVisibility = () => { document.hidden ? clearInterval(timer) : start(); };
      document.addEventListener('visibilitychange', onVisibility);
      reducedMotion.addEventListener('change', start);
      slider.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('[data-b2b-slide]');
        if (!slide) return;
        paused = true;
        if (pauseButton) {
          pauseButton.setAttribute('aria-pressed', 'true');
          pauseButton.textContent = slider.dataset.playLabel;
        }
        show(slides.indexOf(slide));
        clearInterval(timer);
      });
      sliderCleanups.set(slider, () => {
        clearInterval(timer);
        document.removeEventListener('visibilitychange', onVisibility);
        reducedMotion.removeEventListener('change', start);
      });
      show(current);
      start();
    });

    root.querySelectorAll('[data-b2b-tabs]:not([data-b2b-ready])').forEach((tabs) => {
      tabs.dataset.b2bReady = 'true';
      const buttons = [...tabs.querySelectorAll('[role="tab"]')];
      const panels = [...tabs.querySelectorAll('[role="tabpanel"]')];
      const activate = (button) => {
        buttons.forEach((item) => {
          item.setAttribute('aria-selected', item === button ? 'true' : 'false');
          item.tabIndex = item === button ? 0 : -1;
        });
        panels.forEach((panel) => { panel.hidden = panel.id !== button.getAttribute('aria-controls'); });
      };
      buttons.forEach((button, index) => {
        button.addEventListener('click', () => activate(button));
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = event.key === 'ArrowRight' ? (index + 1) % buttons.length : (index - 1 + buttons.length) % buttons.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = buttons.length - 1;
          buttons[next].focus();
          activate(buttons[next]);
        });
      });
      if (buttons.length) activate(buttons.find((button) => button.getAttribute('aria-selected') === 'true') || buttons[0]);
      tabs.addEventListener('shopify:block:select', (event) => {
        const panel = event.target.closest('[role="tabpanel"]');
        const button = buttons.find((item) => item.getAttribute('aria-controls') === panel?.id);
        if (button) activate(button);
      });
    });

    root.querySelectorAll('[data-b2b-carousel]:not([data-b2b-ready])').forEach((carousel) => {
      carousel.dataset.b2bReady = 'true';
      const track = carousel.querySelector('[data-b2b-track]');
      const move = (direction) => track?.scrollBy({ left: direction * Math.max(track.clientWidth * .8, 240), behavior: 'smooth' });
      carousel.querySelector('[data-b2b-prev]')?.addEventListener('click', () => move(-1));
      carousel.querySelector('[data-b2b-next]')?.addEventListener('click', () => move(1));
      if (carousel.dataset.autoplay === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        let timer;
        const start = () => {
          clearInterval(timer);
          timer = setInterval(() => {
            if (!track) return;
            const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
            track.scrollTo({ left: atEnd ? 0 : track.scrollLeft + Math.max(track.clientWidth * .8, 240), behavior: 'smooth' });
          }, Number(carousel.dataset.interval || 4000));
        };
        carousel.addEventListener('mouseenter', () => clearInterval(timer));
        carousel.addEventListener('mouseleave', start);
        start();
      }
    });

    const counters = root.querySelectorAll('[data-b2b-count]:not([data-b2b-ready])');
    if (counters.length) {
      const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const item = entry.target;
        item.dataset.b2bReady = 'true';
        const target = Number(item.dataset.b2bCount);
        if (!Number.isFinite(target) || matchMedia('(prefers-reduced-motion: reduce)').matches) { item.textContent = item.dataset.b2bCount; observer.unobserve(item); return; }
        const start = performance.now();
        const tick = (now) => {
          const progress = Math.min((now - start) / 1100, 1);
          item.textContent = Math.round(target * (1 - Math.pow(1 - progress, 3))).toLocaleString();
          if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        observer.unobserve(item);
      }), { threshold: .35 });
      counters.forEach((counter) => observer.observe(counter));
    }

    root.querySelectorAll('[data-b2b-backtop]:not([data-b2b-ready])').forEach((button) => {
      button.dataset.b2bReady = 'true';
      button.addEventListener('click', () => scrollTo({ top: 0, behavior: 'smooth' }));
    });
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    sliderCleanups.forEach((dispose, slider) => {
      if (event.target.contains(slider)) { dispose(); sliderCleanups.delete(slider); }
    });
  });
})();
